/**
 * ADR-0166 — Pure view-model helpers for the Benchmarks port.
 *
 * This module carries the four maths pieces of the prototype's
 * `buildBenchmarkData` / `BenchmarkCard` (`docs/reference/Foyer_Dashboard_
 * CURRENT.jsx` lines 6697–7259) in shapes the unit tests can pin without
 * mounting React. Copy of the reference is byte-preserved where the
 * output is user-visible (`plainSummary`, verdict labels, round/fmt).
 *
 *   estimatePercentile — piecewise-linear over (P10, median, P90) with
 *     direction awareness for lower-is-better metrics.
 *   chooseVerdict — verdict pill color + label from the percentile band
 *     and directionality.
 *   round / fmt — presentation-precision helpers, unit-aware.
 *   axisScaling — the strip range (minVal, maxVal, span, pct) so the
 *     cohort spread occupies the majority of the strip.
 *   plainSummary — the plain-English at-a-glance sentence.
 *
 * The module is React-free by design so `__tests__/build-benchmark-data.
 * test.ts` can call each helper in isolation.
 */

import type {
  BenchmarkCard,
  BenchmarksResponse,
} from '@sill/types';

// ---------------------------------------------------------------------------
// estimatePercentile — piecewise-linear across (P10, median, P90).
// ---------------------------------------------------------------------------

/**
 * Piecewise-linear percentile estimate. Direction-aware — when
 * `higher_is_better === false` the value is inverted so a low raw value
 * corresponds to a high goodness percentile.
 *
 * Byte-for-byte port of the prototype (Foyer_Dashboard_CURRENT.jsx line
 * 7144). Boundaries: at or below P10 → 5, at or above P90 → 95. Between,
 * interpolated to 10–50 (P10→median) and 50–90 (median→P90). Result is
 * clamped to [0, 100] and rounded to the nearest integer.
 */
export function estimatePercentile(
  yours: number,
  p10: number,
  median: number,
  p90: number,
  higherIsBetter: boolean | null,
): number {
  let p: number;
  if (yours <= p10) p = 5;
  else if (yours <= median) p = 10 + ((yours - p10) / (median - p10)) * 40;
  else if (yours <= p90) p = 50 + ((yours - median) / (p90 - median)) * 40;
  else p = 95;
  if (higherIsBetter === false) p = 100 - p;
  return Math.max(0, Math.min(100, Math.round(p)));
}

// ---------------------------------------------------------------------------
// Verdict pill — color + label from percentile band + directionality.
// ---------------------------------------------------------------------------

/**
 * Theme tokens the verdict chooser consumes. Kept as a narrow interface so
 * the pure helper can accept a token bag without depending on the theme
 * package. In the view we pass `useC()`'s tokens directly.
 */
export interface VerdictTokens {
  readonly limeFg: string;
  readonly amberFg: string;
  readonly coralFg: string;
  readonly muted: string;
}

export interface VerdictOutcome {
  readonly color: string;
  readonly label: string;
}

/**
 * Choose verdict pill color + label from the percentile band and the
 * metric's directionality. Neutral metrics (`higher_is_better === null`,
 * e.g. block_rate) get a raw-position label in neutral grey — no value
 * judgment. Directional metrics get a percentile-band label in
 * lime / amber / coral.
 *
 * Byte-for-byte port of the prototype (Foyer_Dashboard_CURRENT.jsx line
 * 7171). The `unit` argument is only used by the raw-position label's
 * embedded value formatting (matches `fmt`).
 */
export function chooseVerdict(
  yours: number,
  cohortMedian: number,
  percentile: number,
  higherIsBetter: boolean | null,
  tokens: VerdictTokens,
): VerdictOutcome {
  if (higherIsBetter === null) {
    let label: string;
    if (yours > cohortMedian) label = `ABOVE MEDIAN VALUE · P${percentile}`;
    else if (yours < cohortMedian) label = `BELOW MEDIAN VALUE · P${percentile}`;
    else label = `AT MEDIAN · P${percentile}`;
    return { color: tokens.muted, label };
  }
  if (percentile >= 90) return { color: tokens.limeFg, label: `TOP 10% · P${percentile}` };
  if (percentile >= 50) return { color: tokens.limeFg, label: `STRONGER THAN MEDIAN · P${percentile}` };
  if (percentile >= 25) return { color: tokens.amberFg, label: `WEAKER THAN MEDIAN · P${percentile}` };
  return { color: tokens.coralFg, label: `BOTTOM 25% · P${percentile}` };
}

// ---------------------------------------------------------------------------
// round + fmt — presentation-precision helpers.
// ---------------------------------------------------------------------------

/** Rounding rule per unit (byte-preserved from the reference). */
export function round(v: number, unit: BenchmarkCard['unit']): number {
  if (!Number.isFinite(v)) return v;
  if (unit === 'ms') return Math.round(v);
  // % / counts / scores — one decimal
  return Math.round(v * 10) / 10;
}

/** Format for display — appends the unit suffix. */
export function fmt(v: number, unit: BenchmarkCard['unit']): string {
  const r = round(v, unit);
  if (unit === '%') return `${r}%`;
  if (unit === 'ms') return `${r}ms`;
  return `${r}`;
}

// ---------------------------------------------------------------------------
// axisScaling — the strip range.
// ---------------------------------------------------------------------------

export interface AxisScale {
  readonly minVal: number;
  readonly maxVal: number;
  readonly span: number;
  /** Percent along the strip [0, 100] for a given raw value. */
  pct(v: number): number;
}

/**
 * Compute the [minVal, maxVal] window over which the comparison strip
 * renders. Default: [P10 - 25% spread, P90 + 25% spread]. Outlier `yours`
 * extends the appropriate side. `%` / count / `ms` units floor the min at
 * 0. Byte-for-byte port (Foyer_Dashboard_CURRENT.jsx line 7241).
 */
export function computeAxisScale(
  yours: number,
  cohortP10: number,
  cohortP90: number,
  unit: BenchmarkCard['unit'],
): AxisScale {
  const cohortSpread = Math.max(cohortP90 - cohortP10, 0.001);
  const pad = cohortSpread * 0.25;
  let minVal = cohortP10 - pad;
  let maxVal = cohortP90 + pad;
  if (yours < minVal) minVal = yours - cohortSpread * 0.1;
  if (yours > maxVal) maxVal = yours + cohortSpread * 0.1;
  if (unit === '%' || unit === '' || unit === 'ms') {
    if (minVal < 0) minVal = 0;
  }
  const span = Math.max(maxVal - minVal, 0.001);
  return {
    minVal,
    maxVal,
    span,
    pct(v: number): number {
      return Math.max(0, Math.min(100, ((v - minVal) / span) * 100));
    },
  };
}

// ---------------------------------------------------------------------------
// plainSummary — the at-a-glance sentence.
// ---------------------------------------------------------------------------

/**
 * Plain-English summary tying `yours` to the cohort median in
 * unambiguous language. Byte-for-byte port of the reference
 * (Foyer_Dashboard_CURRENT.jsx line 7212).
 *
 *   - neutral metrics (higher_is_better === null): raw-position language
 *     ("2ms higher than the cohort median of 42ms").
 *   - directional metrics: percentile-band language ("In the top 10%…").
 */
export function plainSummary(
  yours: number,
  cohortMedian: number,
  percentile: number,
  higherIsBetter: boolean | null,
  unit: BenchmarkCard['unit'],
): string {
  if (higherIsBetter === null) {
    const diff = Math.abs(yours - cohortMedian);
    const direction =
      yours > cohortMedian ? 'higher' : yours < cohortMedian ? 'lower' : 'equal';
    if (direction === 'equal') {
      return `At the cohort median of ${fmt(cohortMedian, unit)}.`;
    }
    return `${fmt(diff, unit)} ${direction} than the cohort median of ${fmt(cohortMedian, unit)}.`;
  }
  if (percentile >= 90) return `In the top 10% of merchants in this cohort.`;
  if (percentile >= 50) return `Performing better than at least half the cohort.`;
  if (percentile >= 25) return `Trailing the cohort median.`;
  return `In the bottom 25% — worth attention.`;
}

// ---------------------------------------------------------------------------
// View-model shape — mirrors the wire response but with client-computed
// aids (percentile / verdict / axis / summary) already resolved so the
// card's render path stays trivially inspectable.
// ---------------------------------------------------------------------------

export interface BenchmarkCardViewModel {
  readonly card: BenchmarkCard;
  /**
   * Percentile of the merchant within the cohort (0-100). Null when the
   * card's `yours` is null (§Test plan regression guard — the prototype
   * hardcoded 4.2 / 22 / 31 constants MUST NEVER be reached when yours
   * is `null`).
   */
  readonly percentile: number | null;
  /** Verdict pill color + label. Null when yours is null (rendered as
   *  the muted "Not enough data yet" state instead). */
  readonly verdict: VerdictOutcome | null;
  /** Plain-English summary sentence. Null when yours is null. */
  readonly summary: string | null;
}

/** Build a per-card view-model from a wire card. Resolves the four
 *  client-side aids only when `yours` is non-null; when it is null the
 *  card is rendered as the muted "Not enough data yet" state and the
 *  aids are moot. */
export function toCardViewModel(
  card: BenchmarkCard,
  tokens: VerdictTokens,
): BenchmarkCardViewModel {
  if (card.yours === null) {
    return { card, percentile: null, verdict: null, summary: null };
  }
  const percentile = estimatePercentile(
    card.yours,
    card.cohort_p10,
    card.cohort_median,
    card.cohort_p90,
    card.higher_is_better,
  );
  const verdict = chooseVerdict(
    card.yours,
    card.cohort_median,
    percentile,
    card.higher_is_better,
    tokens,
  );
  const summary = plainSummary(
    card.yours,
    card.cohort_median,
    percentile,
    card.higher_is_better,
    card.unit,
  );
  return { card, percentile, verdict, summary };
}

// ---------------------------------------------------------------------------
// Response projection — the wire-response → view-model function the view
// consumes on mount.
// ---------------------------------------------------------------------------

export interface BenchmarksViewModel {
  readonly opt_in_state: 'opted_in' | 'opted_out';
  readonly opted_in_at: string | null;
  readonly opted_out_at: string | null;
  readonly cohort_is_synthetic: true;
  readonly cohort: BenchmarksResponse['cohort'];
  readonly cards: readonly BenchmarkCardViewModel[] | null;
  readonly category_breakdown: BenchmarksResponse['category_breakdown'];
}

/** Project a wire response into the shape the view renders. The heavy
 *  math on each card runs once per (response, tokens) pair; the view
 *  itself is a series of small formatters. */
export function projectBenchmarks(
  response: BenchmarksResponse,
  tokens: VerdictTokens,
): BenchmarksViewModel {
  const cards =
    response.cards === null
      ? null
      : response.cards.map((c) => toCardViewModel(c, tokens));
  return {
    opt_in_state: response.opt_in_state,
    opted_in_at: response.opted_in_at,
    opted_out_at: response.opted_out_at,
    cohort_is_synthetic: response.cohort_is_synthetic,
    cohort: response.cohort,
    cards,
    category_breakdown: response.category_breakdown,
  };
}
